(() => {
  const SELECTORS = {
    modal: '[data-sale-event-modal]',
    title: '.sale-event-modal__title',
    text: '.sale-event-modal__text',
    continueButton: '[data-sale-event-continue]',
    closeButton: '[data-sale-event-close]',
    counter: '[data-counter]',
    counterCurrent: '[data-counter-current]',
    counterTotal: '[data-counter-total]',
    products: '.sale-event-modal__products',
    productsTemplate: '[data-sale-event-products]',
    product: '.sale-event-modal__product',
    productCard: 'product-card[handle]'
  };

  const initSaleEventModal = (modal) => {
    if (!modal || modal.dataset.saleEventInitialized === 'true') {
      return;
    }

    const formId = modal.dataset.formId;
    const form = document.getElementById(formId);

    if (!form) {
      return;
    }

    modal.dataset.saleEventInitialized = 'true';

    const continueButton = modal.querySelector(SELECTORS.continueButton);
    const title = modal.querySelector(SELECTORS.title);
    const textElement = modal.querySelector(SELECTORS.text);
    const closeButtons = Array.from(modal.querySelectorAll(SELECTORS.closeButton));
    const counter = modal.querySelector(SELECTORS.counter);
    const counterCurrent = modal.querySelector(SELECTORS.counterCurrent);
    const counterTotal = modal.querySelector(SELECTORS.counterTotal);
    const productsContainer = modal.querySelector(SELECTORS.products);
    const productsTemplate = modal.querySelector(SELECTORS.productsTemplate);
    const productsLimit = Number(modal.dataset.productsLimit);
    let productCards = Array.from(modal.querySelectorAll(SELECTORS.productCard));
    let poolProducts = [];
    const addedHandles = [];
    const defaultTargetCount = Number( modal.dataset.targetCount || 2);
    const titleTemplateSingular = modal.dataset.titleTemplateSingular || 'Buy [target] and get 1 free';
    const titleTemplatePlural = modal.dataset.titleTemplatePlural || titleTemplateSingular;
    const titleComplete = modal.dataset.titleComplete || 'You get free product';

    const textElementTemplateSingular = modal.dataset.textTemplateSingular || 'Buy [target] and get 1 free';
    const textElementTemplatePlural = modal.dataset.textTemplatePlural || textElementTemplateSingular;
    const roundSize = defaultTargetCount + 1;
    const isContinuous = modal.dataset.targetProducts === 'all';
    let cartTargetCount = 0;
    let remainingTargetCount = defaultTargetCount;
    let lastActiveElement = null;
    let isTitleListenerAttached = false;

    const formatProgressTitle = () => {
      const titleTemplate = remainingTargetCount === 1 ? titleTemplateSingular : titleTemplatePlural;

      if (titleTemplate.includes('[target]')) {
        return titleTemplate.replace('[target]', remainingTargetCount);
      }

      return titleTemplate;
    };

    const formatProgressText = () => {
      const textTemplate = remainingTargetCount === 1 ? textElementTemplateSingular : textElementTemplatePlural;

      textElement.innerHTML = textTemplate.replace('[target]', remainingTargetCount)
        .replace('{', '<span class="as-db-0042-highlight">')
        .replace('}', '</span>');
    };

    const updateTitle = () => {
      if (!title) {
        return;
      }

      title.textContent = remainingTargetCount <= 0
        ? titleComplete
        : formatProgressTitle();
    };

    const updateCounter = () => {
      if (!counter) {
        return;
      }

      const roundCount = roundSize - remainingTargetCount;

      counterCurrent.textContent = isContinuous ? cartTargetCount : roundCount;
      counterTotal.textContent = isContinuous ? cartTargetCount + remainingTargetCount : roundSize;
      counter.style.setProperty('--as-db-0042-fill', String(roundCount / roundSize));
    };

    const updateProgress = () => {
      updateTitle();
      formatProgressText();
      updateCounter();
    };

    const resetTargetCount = async () => {
      cartTargetCount = await getTargetCount();
      remainingTargetCount = roundSize - cartTargetCount % roundSize;
      updateProgress();
    };

    const resetActiveProducts = () => {
      productCards.forEach((card) => card.classList.remove('product-card__active'));
    };

    const highlightAddedProducts = (items = []) => {
      const addedHandles = items
        .map((item) => item?.handle)
        .filter(Boolean);

      if (!addedHandles.length) {
        return;
      }

      productCards.forEach((card) => {
        if (addedHandles.includes(card.getAttribute('handle'))) {
          card.classList.add('product-card__active');
        }
      });
    };

    const handleOf = (product) => product.querySelector(SELECTORS.productCard)?.getAttribute('handle');

    const renderProducts = (products) => {
      poolProducts = products.filter((product, index) => !addedHandles.includes(handleOf(product))
        && products.findIndex((other) => handleOf(other) === handleOf(product)) === index);

      const visibleProducts = poolProducts.slice(0, productsLimit);

      visibleProducts.forEach((product, index) => {
        if (productsContainer.children[index] !== product) {
          productsContainer.insertBefore(product, productsContainer.children[index] || null);
        }
      });
      Array.from(productsContainer.children).slice(visibleProducts.length).forEach((child) => child.remove());

      productCards = Array.from(modal.querySelectorAll(SELECTORS.productCard));
      modal.dispatchEvent(new CustomEvent('sale-event:products-change'));
    };

    const removeAddedProducts = (items = []) => {
      addedHandles.push(...items.map((item) => item?.handle).filter(Boolean));
      renderProducts(poolProducts);
    };

    const openModal = async () => {
      lastActiveElement = document.activeElement;
      await resetTargetCount();
      if(!isContinuous && remainingTargetCount % roundSize === 0) {
        openCartDrawer();
        return;
      }
      resetActiveProducts();
      if (!isTitleListenerAttached) {
        document.addEventListener('variant:add', handleUpdateTitle);
        isTitleListenerAttached = true;
      }
      modal.classList.add('is-visible');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('overflow-hidden');
    };

    const closeModal = () => {
      if (isTitleListenerAttached) {
        document.removeEventListener('variant:add', handleUpdateTitle);
        isTitleListenerAttached = false;
      }
      modal.classList.remove('is-visible');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('overflow-hidden');

      if (lastActiveElement instanceof HTMLElement) {
        lastActiveElement.focus();
      }
    };

    const openCartDrawer = () => {
      const drawer = document.querySelector('cart-drawer');

      if (drawer && typeof drawer.show === 'function') {
        drawer.show();
      }
    };

    async function handleUpdateTitle(event) {
      if (event.target === form) return;

      if (modal.classList.contains('is-visible')) {
        if (productsTemplate) {
          const items = event.detail?.items || [];
          const quickBuyDrawer = event.target.closest('quick-buy-drawer');

          if (quickBuyDrawer) {
            quickBuyDrawer.addEventListener('dialog:after-hide', () => removeAddedProducts(items), { once: true });
          } else {
            removeAddedProducts(items);
          }
        }

        if (isContinuous) {
          await resetTargetCount();
        } else {
          remainingTargetCount = Math.max(remainingTargetCount - 1, 0);
          updateProgress();
        }
        highlightAddedProducts(event.detail?.items || []);

        if (remainingTargetCount === 0) {
          closeModal();
          openCartDrawer();
        }
      }
    }

    async function getTargetCount () {
      const cartData = await (await fetch('/cart.json')).json()
      const targetProducts = modal.dataset.targetProducts
      const productsId = targetProducts.split(',').map(id => +id)

      return cartData.items.reduce((acc, item) => {
        const isDiscounted = targetProducts === 'all'
          ? !item.gift_card
          : productsId.includes(item.product_id)

        if(isDiscounted) return acc + item.quantity

        return acc
      }, 0)
    }

    async function loadRecommendations () {
      const url = modal.dataset.recommendationsUrl

      if (!url) return

      try {
        const response = await fetch(url)

        if (!response.ok) return

        const html = new DOMParser().parseFromString(await response.text(), 'text/html')
        const collectionHandles = (modal.dataset.collectionHandles || '').split(',')
        const isInCollection = (product) => collectionHandles.includes(handleOf(product))
        const gendersOf = (element) => (element.dataset.gender || '').split(',').filter(Boolean)
        const currentGenders = gendersOf(modal)
        const matchesGender = (product) => {
          const genders = gendersOf(product)

          return !currentGenders.length || !genders.length || genders.some((gender) => currentGenders.includes(gender))
        }
        const recommendedProducts = Array.from(html.querySelectorAll(SELECTORS.product)).filter(matchesGender)

        renderProducts([
          ...recommendedProducts.filter(isInCollection),
          ...poolProducts,
          ...recommendedProducts.filter((product) => !isInCollection(product))
        ])
      } catch (error) {
        console.error('[sale-event-modal]', error)
      }
    }

    if (productsTemplate) {
      renderProducts(Array.from(document.importNode(productsTemplate.content, true).children));
    }

    loadRecommendations();

    form.addEventListener('variant:add', (event) => {
      openModal();
    });

    modal.addEventListener('variant:add', (event) => {
      if (event.target !== form) {
        event.detail.blockCartDrawerOpening = true;
      }
    });

    if (continueButton) {
      continueButton.addEventListener('click', () => {
        closeModal();
        openCartDrawer();
      });
    }

    closeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        closeModal();
        openCartDrawer();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-visible')) {
        closeModal();
        openCartDrawer();
      }
    });
  };

  const initAllSaleEventModals = () => {
    document.querySelectorAll(SELECTORS.modal).forEach(initSaleEventModal);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSaleEventModals);
  } else {
    initAllSaleEventModals();
  }
})();
